/* eslint-disable react-refresh/only-export-components -- context provider and its consumer hook form one public module */
/**
 * AuthContext · 全局当前用户 + tenant 状态.
 *
 * 用法 (内层组件):
 *   const { user, tenant, isAuthenticated, refresh, logout } = useAuth();
 *
 * 用法 (App 顶层):
 *   <AuthProvider>
 *     <AppRoutes />
 *   </AuthProvider>
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  clearTokens,
  getMe,
  getAccessToken,
  isLoggedIn,
  logout as apiLogout,
  type TenantInfo,
  type UserInfo,
} from "@/api/auth";
import { flushSync } from "react-dom";
import { clearQuoteJourneySession } from "@/components/quote/journey/quoteJourneySession";
import { quoteJourneyScope } from "@/components/quote/journey/quoteJourneyState";
import { clearMru } from "@/modules/drawing/drawingMru";

interface AuthState {
  user: UserInfo | null;
  tenant: TenantInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** 重新拉一次 /me · 登录/改用户信息后调. */
  refresh: () => Promise<void>;
  /** 直接设当前用户 · 登录/注册/接受邀请成功时调. */
  setUser: (u: UserInfo) => void;
  /** 登出 · 清 token + 后端审计 + 跳 /login. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const requestEpoch = useRef(0);
  const invalidateRequests = useCallback(() => {
    requestEpoch.current++;
  }, []);

  const refresh = useCallback(async () => {
    const epoch = ++requestEpoch.current,
      token = getAccessToken();
    if (!isLoggedIn()) {
      setUserState(null);
      setIsLoading(false);
      return;
    }
    try {
      const u = await getMe();
      if (epoch !== requestEpoch.current || token !== getAccessToken()) return;
      setUserState(u);
    } catch {
      // 旧请求不能清掉后来登录用户的身份和凭证。
      if (epoch !== requestEpoch.current || token !== getAccessToken()) return;
      setUserState(null);
      clearTokens();
    } finally {
      if (epoch === requestEpoch.current) setIsLoading(false);
    }
  }, []);

  // 首次挂载: 如果有 token 就拉用户信息
  useEffect(() => {
    void refresh();
    return invalidateRequests;
  }, [refresh, invalidateRequests]);

  const setUser = useCallback((u: UserInfo) => {
    requestEpoch.current++;
    setUserState(u);
    setIsLoading(false);
  }, []);

  const finishLocalLogout = useCallback(async () => {
    requestEpoch.current++;
    clearMru(user?.id ?? null);
    const scope =
      user?.tenant?.id && user.id
        ? quoteJourneyScope(user.tenant.id, user.id)
        : null;
    // 同步卸载取消旧保存，再等File事务与取消清理结束，避免另一页迟到写回。
    flushSync(() => setUserState(null));
    const cleared = await clearQuoteJourneySession(scope);
    window.location.href = cleared ? "/login" : "/login?local_cleanup=failed";
  }, [user?.id, user?.tenant?.id]);
  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      await finishLocalLogout();
    }
  }, [finishLocalLogout]);
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== window.localStorage ||
        (event.key !== null && event.key !== "cnc.access_token")
      )
        return;
      if (!isLoggedIn()) void finishLocalLogout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [finishLocalLogout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant: user?.tenant ?? null,
        isAuthenticated: user !== null,
        isLoading,
        refresh,
        setUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
